/** @jsxImportSource theme-ui */
import { Title } from "@bottlebooks/gatsby-design-system";
import { Box, Col, Container, Row } from "@bottlebooks/gatsby-theme-base/src";
import { Trans } from "@lingui/macro";
import { FragmentType, graphql, useFragment } from "~/gql";
import useLink from "../../useLink";
import BrandAddressSection from "../Brand/BrandAddressSection.next";
import BrandBanner from "../Brand/BrandBanner";
import BrandDetailsSection from "../Brand/BrandDetailsSection.next";
import BrandDistributors from "../Brand/BrandDistributors.next";
import BrandShopsSection from "../Brand/BrandShopsSection";
import ContentRow from "../ContentRow";
import ExhibitorContacts from "../ExhibitorPage/ExhibitorContacts.next";
import ProducerActions from "../Producer/ProducerActions";
import ProductListListRow from "../ProductListsList/ProductListListRow";
import ProductListsList from "../ProductListsList/ProductListsList";
import ProductCard from "../ProductCard/ProductCard.next";
import ProductsList from "../ProductsList/ProductsList";
import useSiteConfig from "../useSiteConfig";
import ProducerHeaderRow from "./ProducerHeaderRow";

export const fragment = graphql(/* GraphQL */ `
  fragment RegisteredBrand_ProducerPage on RegisteredBrand {
    ...RegisteredBrand_ProducerHeaderRow
    ...ProducerFilters_RegisteredBrand
    ...BrandDetailsSection_RegisteredBrand
    profile {
      ...BrandDistributors
    }
    products {
      nodes {
        ...ProductCard_RegisteredProduct
        productId
        ...ProductListRow
      }
    }
    registration {
      ...ExhibitorSections
      ...RegistrationContacts
      ...Registration_ProducerHeaderRow
      profile {
        ...BrandDetailsSection_Profile
        ...BrandDistributors
        ...BrandAddressSection_Profile
        ...BrandShopsSection
      }
    }
  }
`);

export default function ProducerPage({
  // productLists,
  previous,
  next,
  // locale,
  event,
  data,
}: {
  event: unknown;
  data: FragmentType<typeof fragment>;
  // productLists: unknown[];
  previous: string;
  next: string;
  // locale: string;
}) {
  const registeredBrand = useFragment(fragment, data);
  const products = registeredBrand?.products?.nodes;
  const link = useLink();
  const { showDistributionDetails } = useSiteConfig();
  const hasProducts = products && products.length !== 0;
  const hasProductLists = false; // productLists && productLists.length !== 0;
  const hasProductsSection = hasProducts || hasProductLists;
  const producer = { ...registeredBrand, ...registeredBrand.profile };
  const exhibitor = {
    ...registeredBrand.registration,
    ...registeredBrand.registration.profile,
  };
  return (
    <Box>
      <BrandBanner {...producer} />
      <ProducerHeaderRow
        exhibitor={registeredBrand.registration.profile}
        producer={producer}
        previous={previous}
        next={next}
      />

      <Container>
        <Row
          sx={{
            paddingY: 3,
            paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
          }}
        >
          <Col>
            <ProducerActions producer={producer} />
          </Col>
        </Row>
        {/* {producer.registrationCount === 1 && ( */}
        {/* TODO: this will need to be resolved when multiple registrations show
        products from the same producer. Or, we get rid of producer pages altogether. */}
        <ContentRow>
          <Col flex>
            <ExhibitorContacts
              exhibitor={exhibitor}
              eventId={event.eventId}
              sx={{ flex: "1 0 auto" }}
            />
          </Col>
        </ContentRow>
        {/* )} */}
        <Row
          sx={{
            paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
          }}
        >
          <BrandDetailsSection
            data={registeredBrand.profile}
            presentedBrandsData={registeredBrand.profile}
          />
        </Row>
        {["all", "global"].includes(showDistributionDetails) && (
          <ContentRow>
            <Col flex>
              <BrandDistributors brand={producer} />
            </Col>
          </ContentRow>
        )}
      </Container>

      <BrandAddressSection data={producer} />
      <Container
        sx={{
          paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
        }}
      >
        <BrandShopsSection brand={producer} />
      </Container>

      {hasProductsSection ? (
        <Container fluid sx={{ maxWidth: "container.fluid" }}>
          <Title sx={{ marginBottom: 4 }}>
            <Trans>Products</Trans>
          </Title>
          {hasProductLists && (
            <ProductListsList sx={{ marginX: [-3, -2], marginY: -2 }}>
              {productLists.map((productList) => (
                <ProductListListRow
                  key={productList.id}
                  productList={productList}
                  to={link.productList(productList)}
                  sx={{ marginX: [0, 2], marginY: [0, 2] }}
                />
              ))}
            </ProductListsList>
          )}

          {hasProducts && (
            <ProductsList
              count={products.length}
              sx={{ marginX: [-3, -2], marginY: -2 }}
            >
              {products.map((registeredProduct) => (
                <ProductCard
                  key={registeredProduct.productId}
                  product={registeredProduct}
                  sx={{ marginBottom: [null, null, 3] }}
                />
              ))}
            </ProductsList>
          )}
        </Container>
      ) : null}
    </Box>
  );
}
